import {
  Box,
  ExpandableSection,
  ExpandableSectionProps,
  NonCancelableCustomEvent,
} from '@amzn/awsui-components-core';
import { ContentNode, ContentNodeType } from '@amzn/awscat-pattern-library-service-model';
import { TiptapOutputType } from '@amzn/awscat-editor';
import React, { useContext, useState } from 'react';
import '../../../resources/styles/scss/components/workflow-viewer/workflow-viewer.scss';
import constants from '../../../common/constants';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import { ContentPageTitleInput } from '../../content/ContentPageTitleInput';
import { ContentViewerContext } from '../ContentViewerContext';
import { ContentPagesTree, TreeStatus } from './ContentPagesTree';

export const AddNewContentPageButton = () => {
  const viewerCtx = useContext(ContentViewerContext);

  const [title, setTitle] = useState<TiptapOutputType>('');
  const [isTitleValid, setIsTitleValid] = useState(false);
  const [showTitleValidationError, setShowTitleValidationError] = useState<boolean>();
  const [parentPage, setParentPage] = useState<ContentNode>();
  const [parentPageError, setParentPageError] = useState<string>();
  const [isParentPageSelectionExpanded, setIsParentPageSelectionExpanded] = useState(false);
  const [parentPageSelectionTreeStatus, setParentPageSelectionTreeStatus] = useState(
    TreeStatus.Default,
  );
  const [isVisible, setIsVisible] = useState<boolean>(false);

  const updateTitle = (newTitle: TiptapOutputType, error?: string) => {
    setTitle(newTitle);
    setIsTitleValid(!error);
    if (error) {
      setShowTitleValidationError(true);
    }
  };

  const updateParentPage = (page: ContentNode, depth: number) => {
    if (depth >= 4) {
      setParentPageError('Only 5 levels of nested pages allowed.');
    } else {
      setParentPageError(undefined);
    }

    setParentPage(page);
  };

  const add = () => {
    if (isTitleValid) {
      const newPage = ContentNode.create(title, '', ContentNodeType.Page);
      // @ts-expect-error TS2722 - strict migration
      viewerCtx.addNewPage(newPage, parentPage);
    }
  };

  const resetForm = () => {
    setParentPage(undefined);
    // @ts-expect-error TS2345 - strict migration
    setTitle(undefined);
    setIsTitleValid(false);
    setIsParentPageSelectionExpanded(false);
    setShowTitleValidationError(false);

    setParentPageSelectionTreeStatus(TreeStatus.CollapseAll);
    setParentPageSelectionTreeStatus(TreeStatus.Default);
    setIsVisible(false);
  };

  const changeParentPageSelectionExpanded = (
    event: NonCancelableCustomEvent<ExpandableSectionProps.ChangeDetail>,
  ) => {
    if (isParentPageSelectionExpanded && parentPage) {
      setParentPage(undefined);
    }
    setIsParentPageSelectionExpanded(event.detail.expanded);
  };

  const showForm = async () => {
    setIsVisible(true);
    return true;
  };

  const contentPageForm = isVisible ? (
    <>
      <ContentPageTitleInput
        title={title}
        onUpdated={updateTitle}
        showValidationError={showTitleValidationError}
      />
      <Box variant="div" margin={{ top: 'm' }}>
        <ExpandableSection
          header={
            <span>
              Select a parent page <span className="light-grey-color">(optional)</span>
            </span>
          }
          variant="navigation"
          expanded={isParentPageSelectionExpanded}
          onChange={changeParentPageSelectionExpanded}
        >
          <Box variant="div" margin={{ top: 'm', right: 's' }}>
            <ContentPagesTree
              // @ts-expect-error TS2322 - strict migration
              content={viewerCtx.content}
              nodeClassName="parent-page-item"
              selectedNodeClassName="parent-page-selected"
              selectedNode={parentPage}
              onSelectedNodeChanged={updateParentPage}
              treeStatus={parentPageSelectionTreeStatus}
            />
          </Box>
        </ExpandableSection>
      </Box>
      {parentPageError && <Box color="text-status-error">{parentPageError}</Box>}
    </>
  ) : null;

  return (
    <ModalDialogButtonV3
      iconName="add-plus"
      iconAlign="left"
      header={constants.ADD_NEW_PAGE_HEADER}
      buttonText={constants.ADD_NEW_PAGE_BUTTON_TEXT}
      submitButtonText={constants.ADD_NEW_PAGE_SUBMIT_BUTTON_TEXT}
      inputValid={isTitleValid && !parentPageError}
      onSubmit={add}
      onDismiss={resetForm}
      onBeforeOpenModal={showForm}
      dataTestId="edit-guide-add-new-page"
    >
      {contentPageForm}
    </ModalDialogButtonV3>
  );
};
